// The error, zero-data and zero-results surfaces, each in one full-width row of the body. The content
// centers in the visible part of the table region and stays there while the table scrolls sideways; the
// row itself stays in normal vertical flow.

import React, { isValidElement, useRef } from 'react'
import PropTypes from 'prop-types'

import { Button } from '@CommonComponents/buttons'
import { useTable } from '../context'
import { ERROR_COPY, zeroResultsCopy } from './copy'
import { useVisibleWidth } from './useVisibleWidth'

// one level below the table title, so the outline stays in order
const headingTag = level => `h${Math.min((Number(level) || 2) + 1, 6)}`

/** One full-width body row whose content stays centered in the visible part of the region. */
export const StateRow = ({ children, className = '' }) => {
  const { layout, geometry } = useTable()
  const viewRef = useRef(null)
  const width = useVisibleWidth(viewRef, geometry && geometry.visibleWidth)
  return (
    <tr className={`me-table-state${className ? ` ${className}` : ''}`}>
      <td colSpan={layout.columnCount}>
        <div
          ref={viewRef}
          className="me-table-state-view"
          style={width ? { '--me-table-state-view-w': `${width}px` } : null}
        >
          <div className="me-table-state-body">{children}</div>
        </div>
      </td>
    </tr>
  )
}

StateRow.propTypes = {
  children: PropTypes.node,
  // added beside me-table-state, for a row that refines the state row
  className: PropTypes.string,
}

const StateTitle = ({ children }) => {
  const { config } = useTable()
  const Tag = headingTag(config.headingLevel)
  // focusable so focus can land here when the control that led to this state is gone
  return (
    <Tag className="me-table-state-title" tabIndex={-1}>
      {children}
    </Tag>
  )
}

StateTitle.propTypes = { children: PropTypes.node }

const ErrorState = () => {
  const { retry } = useTable()
  return (
    <StateRow>
      <i
        className="fa-regular fa-triangle-exclamation me-table-state-icon"
        aria-hidden="true"
      />
      <StateTitle>{ERROR_COPY.title}</StateTitle>
      <p className="me-table-state-description">{ERROR_COPY.description}</p>
      <Button
        className="me-btn me-btn-secondary me-btn-sm"
        onClick={() => retry()}
      >
        {ERROR_COPY.action}
      </Button>
    </StateRow>
  )
}

const ZeroDataAction = ({ action }) => {
  // a consumer-composed node renders unchanged
  if (isValidElement(action)) return action
  return (
    <Button
      className="me-btn me-btn-primary"
      href={action.href}
      icon={action.icon}
      onClick={action.onSelect}
    >
      {action.label}
    </Button>
  )
}

ZeroDataAction.propTypes = {
  action: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onSelect: PropTypes.func,
      href: PropTypes.string,
      icon: PropTypes.string,
    }),
  ]).isRequired,
}

const ZeroData = () => {
  const { config } = useTable()
  const empty = config.emptyState
  return (
    <StateRow>
      <StateTitle>{empty.title}</StateTitle>
      {empty.description && (
        <p className="me-table-state-description">{empty.description}</p>
      )}
      {empty.action && (
        <div className="me-table-state-action">
          <ZeroDataAction action={empty.action} />
        </div>
      )}
    </StateRow>
  )
}

const ZeroResults = () => {
  const { clearQuery, layout } = useTable()
  const copy = zeroResultsCopy(layout.filterColumns.length > 0)
  return (
    <StateRow>
      <StateTitle>{copy.title}</StateTitle>
      <Button
        className="me-btn me-btn-secondary me-btn-sm"
        onClick={() => clearQuery()}
      >
        {copy.action}
      </Button>
    </StateRow>
  )
}

const SURFACES = {
  error: ErrorState,
  'zero-data': ZeroData,
  'zero-results': ZeroResults,
}

export const StateSurface = ({ kind }) => {
  const Surface = SURFACES[kind]
  return <Surface />
}

StateSurface.propTypes = {
  kind: PropTypes.oneOf(Object.keys(SURFACES)).isRequired,
}
